import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-morphism border-b border-border/50 safe-area-top">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-bold">Privacy Policy</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 safe-area-bottom">
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground text-sm mb-8">
            Sidst opdateret: December 2025
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">1. Introduktion</h2>
            <p className="text-muted-foreground">
              Vinyllog ("vi", "os" eller "vores") respekterer dit privatliv og er forpligtet til at beskytte dine personlige data. 
              Denne privatlivspolitik forklarer, hvordan vi indsamler, bruger og beskytter dine oplysninger, når du bruger vores app.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">2. Data vi indsamler</h2>
            <h3 className="text-lg font-medium mb-2">Kontooplysninger</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>Email-adresse (til login og kommunikation)</li>
              <li>Adgangskode (krypteret og sikker)</li>
              <li>Land (til valutavisning)</li>
            </ul>
            
            <h3 className="text-lg font-medium mb-2">Samlingsdata</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-4">
              <li>Vinylplader du tilføjer til din samling</li>
              <li>Tilstandsvurderinger (vinyl og cover)</li>
              <li>Noter og salgspriser</li>
              <li>Ønskeliste-elementer</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">Tekniske data</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Enheds-type og browser</li>
              <li>IP-adresse</li>
              <li>Brugsstatistik (via anonymiseret analytics)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">3. Sådan bruger vi dine data</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>At levere og forbedre vores tjeneste</li>
              <li>At gemme og synkronisere din vinylsamling</li>
              <li>At vise markedsværdier fra Discogs</li>
              <li>At sende vigtige meddelelser (nulstilling af adgangskode, osv.)</li>
              <li>At behandle betalinger for PRO-abonnement</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">4. Tredjepartstjenester</h2>
            <p className="text-muted-foreground mb-4">Vi bruger følgende tredjepartstjenester:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li><strong>Discogs API:</strong> Til at hente albumdata og markedsværdier</li>
              <li><strong>Stripe:</strong> Til sikker betalingsbehandling</li>
              <li><strong>Resend:</strong> Til at sende emails</li>
              <li><strong>PostHog:</strong> Til anonymiseret brugsanalyse</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">5. Datasikkerhed</h2>
            <p className="text-muted-foreground">
              Vi bruger industristandarder til at beskytte dine data, herunder:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
              <li>HTTPS-kryptering på alle forbindelser</li>
              <li>Sikker adgangskodehashing (bcrypt)</li>
              <li>JWT-tokens til autentificering</li>
              <li>Sikker cloud-hosting</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">6. Kameraadgang</h2>
            <p className="text-muted-foreground">
              Vinyllog anmoder om adgang til dit kamera udelukkende til stregkodescanning. 
              Vi optager ikke, gemmer ikke eller deler ikke billeder eller video fra dit kamera.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">7. Dine rettigheder</h2>
            <p className="text-muted-foreground mb-2">Du har ret til at:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>Få adgang til dine data</li>
              <li>Rette dine data</li>
              <li>Slette din konto og alle tilknyttede data</li>
              <li>Eksportere dine data (PRO-funktion)</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Kontakt os på <a href="mailto:support@vinyllog.app" className="text-primary underline">support@vinyllog.app</a> for at udøve disse rettigheder.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">8. Cookies</h2>
            <p className="text-muted-foreground">
              Vi bruger kun nødvendige cookies til at holde dig logget ind. 
              Vi sælger ikke dine data til tredjeparter.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">9. Børns privatliv</h2>
            <p className="text-muted-foreground">
              Vinyllog er ikke beregnet til børn under 13 år. 
              Vi indsamler ikke bevidst oplysninger fra børn under 13 år.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">10. Ændringer til denne politik</h2>
            <p className="text-muted-foreground">
              Vi kan opdatere denne privatlivspolitik fra tid til anden. 
              Vi vil underrette dig om væsentlige ændringer via email eller en besked i appen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">11. Kontakt</h2>
            <p className="text-muted-foreground">
              Hvis du har spørgsmål om denne privatlivspolitik, kontakt os på:
            </p>
            <p className="text-muted-foreground mt-2">
              <strong>Email:</strong>{" "}
              <a href="mailto:support@vinyllog.app" className="text-primary underline">
                support@vinyllog.app
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
